/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { Alchemy } from '@blend/alchemy';

import { BlendBridge } from 'blb-types/blendBridge';
import { theme as grommetTheme } from 'blb-components/GrommetComponents';

import EditTeamInfoModal from './EditTeamInfoModal';
import DeleteTeamModal from './DeleteTeamModal';
import TeamsTable from './TeamsTable/TeamsTable';
import TeamsPageEmptyState from './TeamsPageEmptyState';
import TeamsPageErrorState from './TeamsPageErrorState';
import InviteTeamMembersModal from './InviteTeamMembersModal';

interface TeamsPageProps {
  error?: Error;
  refetchTeams: () => Promise<void>;
  teams: BlendBridge.API.SearchGroupRecord[];
}

const TeamsPageContent: React.FC<TeamsPageProps> = ({ error, refetchTeams, teams }) => {
  const [teamToEdit, setTeamToEdit] = useState<BlendBridge.API.SearchGroupRecord | null>(null);
  const [teamToDelete, setTeamToDelete] = useState<BlendBridge.API.SearchGroupRecord | null>(null);
  const [teamForInviteMembersModal, setTeamForInviteMembersModal] = useState<BlendBridge.API.SearchGroupRecord | null>(
    null,
  );

  if (error) {
    return <TeamsPageErrorState error={error} />;
  }

  if (teams.length === 0) {
    return <TeamsPageEmptyState />;
  }

  const closeEditTeamInfoModal = async (shouldRefetchTeams?: boolean) => {
    setTeamToEdit(null);
    if (shouldRefetchTeams) {
      await refetchTeams();
    }
  };

  const closeDeleteTeamModal = async (shouldRefetchTeams?: boolean) => {
    setTeamToDelete(null);
    if (shouldRefetchTeams) {
      await refetchTeams();
    }
  };

  const closeInviteMembersModal = async (shouldRefetchTeams?: boolean) => {
    setTeamForInviteMembersModal(null);
    if (shouldRefetchTeams) {
      await refetchTeams();
    }
  };

  return (
    <Alchemy grommetTheme={grommetTheme}>
      <TeamsTable
        refetchTeams={refetchTeams}
        setTeamForInviteMembersModal={setTeamForInviteMembersModal}
        setTeamToDelete={setTeamToDelete}
        setTeamToEdit={setTeamToEdit}
        teams={teams}
      />
      {teamToEdit && <EditTeamInfoModal handleClose={closeEditTeamInfoModal} team={teamToEdit} />}
      {teamToDelete && <DeleteTeamModal handleClose={closeDeleteTeamModal} team={teamToDelete} />}
      {teamForInviteMembersModal && (
        <InviteTeamMembersModal handleClose={closeInviteMembersModal} team={teamForInviteMembersModal} />
      )}
    </Alchemy>
  );
};

export default TeamsPageContent;
